import {AxiosRequestConfig, AxiosResponse} from 'axios';
import {IAddNewTagBody} from '../components/eca-doc-list/bulk-tagging/AddNewTag';
import {
  IDocListExportReqBody,
  IDocListReqBody,
  IFilterSet,
  ISavedSearchReqBody,
} from '../components/eca-doc-list/eca-doc-list.util';
import {IAddNewGroupReqBody} from '../components/eca-doc-list/eca-save-search/EcaSaveSearch';
import {IAnalyzerReqBody} from '../components/indexing/CustomIndex/CreateCustomIndex/CustomIndexStep3';
import {ISearchRequestBody} from '../components/pages/index-search/IndexSearchPage';
import {IUpdateTagNamesBody} from '../hooks/eca-tagging/useUpdateTagNames';
import {CustodiansResponse} from '../hooks/index-search/useCustodians';
import apiClient from './index';

const version = 'v1';
const ecaEndpoint = (path: string) => `${version}/eca${path}`;
export const getIndex = (params?: string) => {
  return apiClient.get(ecaEndpoint('/indexes' + params));
};

export const getNlpIndexSummary = (params?: string) => {
  return apiClient.get(ecaEndpoint('/indexes/nlp/summary' + params));
};

export const getIndexByName = (params: string, fieldTemplateId: string) => {
  return apiClient.get(ecaEndpoint(`/indexes/${fieldTemplateId}` + params));
};

export const runNlpSearch = (
  params: string,
  name: string,
  body: Record<string, unknown>,
  signal?: AbortSignal
) => {
  return apiClient.post(
    ecaEndpoint(`/indexes/${name}/searches/nlp/run` + params),
    body,
    {signal: signal}
  );
};

export const getCustodians = (
  clientId: string,
  projectId: string,
  params: Record<string, string | number> = {}
): Promise<AxiosResponse<CustodiansResponse>> => {
  return apiClient.get(
    ecaEndpoint(`/custodians?clientId=${clientId}&projectId=${projectId}`),
    {params}
  );
};

export const createECACustomField = (
  clientId: string,
  projectId: string,
  body: Record<string, unknown>
): Promise<AxiosResponse<Blob>> => {
  return apiClient.post<Blob>(
    ecaEndpoint(`/tagfield?clientId=${clientId}&projectId=${projectId}`),
    body
  );
};

export const createECAMetaDataField = (
  clientId: string,
  projectId: string,
  body: Record<string, unknown>
): Promise<AxiosResponse<Blob>> => {
  return apiClient.post<Blob>(
    ecaEndpoint(`/field?clientId=${clientId}&projectId=${projectId}`),
    body
  );
};

export const updateECAFieldDetail = (
  clientId: string,
  projectId: string,
  body: Record<string, unknown>
): Promise<AxiosResponse<CustodiansResponse>> => {
  return apiClient.put(
    ecaEndpoint(`/field?clientId=${clientId}&projectId=${projectId}`),
    body
  );
};

export const getECAFieldDetail = (
  fieldId: number,
  clientId: string,
  projectId: string
): Promise<AxiosResponse<CustodiansResponse>> => {
  return apiClient.get(
    ecaEndpoint(`/field/${fieldId}?clientId=${clientId}&projectId=${projectId}`)
  );
};

export const ECAFieldTags = (
  method: 'post' | 'put' | 'delete',
  fieldId: number,
  clientId: string,
  projectId: string,
  body?: Record<string, unknown>[]
): Promise<AxiosResponse<Blob>> => {
  if (method === 'post') {
    return apiClient.post<Blob>(
      ecaEndpoint(
        `/${fieldId}/tag?clientId=${clientId}&projectId=${projectId}`
      ),
      body
    );
  } else if (method === 'put') {
    return apiClient.put<Blob>(
      ecaEndpoint(
        `/${fieldId}/tag?clientId=${clientId}&projectId=${projectId}`
      ),
      body
    );
  } else {
    return apiClient.delete<Blob>(
      ecaEndpoint(
        `/${fieldId}/tag?tagId=${body?.[0].id}&clientId=${clientId}&projectId=${projectId}`
      )
    );
  }
};

export const getIndexHistory = (params: string, indexId: number) => {
  return apiClient.get(ecaEndpoint(`/indexes/${indexId}/history` + params));
};

export const reRunBuildHandler = (params: string, indexId: number) => {
  return apiClient.post(
    ecaEndpoint(`/indexes/${indexId}/faileddocs/rerun` + params)
  );
};

export const retryNlpDocuments = (
  projectCode: string,
  ecaNlpIngestionId: number
) => {
  return apiClient.post(
    ecaEndpoint(
      `/indexes/nlp/retry?projectCode=${encodeURIComponent(
        projectCode
      )}&ecaNlpIngestionId=${ecaNlpIngestionId}`
    ),
    {
      DeleteType: 1,
      IdsToDelete: ['1'],
    }
  );
};

export const retryOverlay = (path: string, params: string, reqBody?: any) => {
  // Path: overlay, mediarefresh, ap/mediarefresh
  return apiClient.post(
    ecaEndpoint(`/indexes/${path}/retry` + params),
    reqBody
  );
};

export const getDownloadIndexError = async (
  params: string,
  indexId: number,
  ecaNlpingestionId?: number
): Promise<AxiosResponse<Blob>> => {
  const nlpIngestionParam =
    ecaNlpingestionId && ecaNlpingestionId > 0
      ? `&ecaNlpIngestionId=${ecaNlpingestionId}`
      : '';

  return await apiClient.get<Blob>(
    ecaEndpoint(
      `/indexes/${indexId}/faileddocs/download${params}${nlpIngestionParam}`
    ),
    {
      responseType: 'blob',
    }
  );
};

export const RebuildIndex = (params: string, fieldTemplateId: number) => {
  return apiClient.post(
    ecaEndpoint(`/indexes/${fieldTemplateId}/rebuild` + params)
  );
};
export const GenerateSummary = (params: string, documentId: string) => {
  return apiClient.post(
    ecaEndpoint(`/searches/document/${documentId}/summary` + params)
  );
};
export const fetchSummary = (params: string, documentId: string) => {
  return apiClient.get(
    ecaEndpoint(`/searches/document/${documentId}/summary` + params)
  );
};

export const getIndexByClientIdAndProjectId = (
  clientId: string,
  projectId: string
) => {
  return apiClient.get(
    ecaEndpoint(`/indexes?clientId=${clientId}&projectId=${projectId}`)
  );
};

export const getRelationalGroups = (params: string) => {
  return apiClient.get(ecaEndpoint(`/relational-groups` + params));
};

export const getAnalyzers = (params: string, fieldTemplateId: number) => {
  return apiClient.get(
    ecaEndpoint(`/indexes/${fieldTemplateId}/analyzers` + params)
  );
};

export const createAnalyzer = (params: string, reqBody: IAnalyzerReqBody) => {
  return apiClient.post(ecaEndpoint(`/indexes/analyzer` + params), reqBody);
};

export const getSearchId = async (
  params: string,
  indexName: string,
  reqBody: ISearchRequestBody,
  signal?: AbortSignal
) => {
  const result = await apiClient.post(
    ecaEndpoint(`/indexes/${indexName}/searches/run` + params),
    reqBody,
    {signal: signal}
  );
  return result.data;
};

export const getDocListMetadata = async (
  params: string,
  fieldTemplateId: number
) => {
  const result = await apiClient.get(
    ecaEndpoint(`/indexes/${fieldTemplateId}/metadata` + params)
  );
  return result.data;
};

export const getEcaFields = (params?: string, signal?: AbortSignal) => {
  return apiClient.get(ecaEndpoint(`/field?${params}`), {
    signal: signal,
  });
};
export const getExportFields = (params?: string) => {
  return apiClient.get(ecaEndpoint(`/export/list?${params}`));
};
export const DownloadExportCsv = (params?: string) => {
  return apiClient.get(ecaEndpoint(`/export/csv?${params}`));
};
export const getExportDetailsView = (exportId: string, params?: string) => {
  return apiClient.get(ecaEndpoint(`/export/${exportId}?${params}`));
};

export const createCustomIndex = async (params: string, reqBody: any) => {
  const result = await apiClient.post(
    ecaEndpoint('/indexes' + params),
    reqBody
  );
  return result.data;
};

export const getAllDocResults = async (
  params: string,
  indexName: string,
  reqBody: IDocListReqBody,
  signal?: AbortSignal
) => {
  const result = await apiClient.post(
    ecaEndpoint(`/indexes/${indexName}/alldoclist` + params),
    normalizeDocumentIdFilter(reqBody),
    {signal: signal}
  );
  return result.data;
};

export const getSearchResults = async (
  params: string,
  searchId: string,
  reqBody: IDocListReqBody,
  signal?: AbortSignal
) => {
  const result = await apiClient.post(
    ecaEndpoint(`/searches/${searchId}/results` + params),
    normalizeDocumentIdFilter(reqBody),
    {signal: signal}
  );
  return result.data;
};

export const getFieldGridCSVData = async (params: string) => {
  const result = await apiClient.get(ecaEndpoint(`/field/export` + params));
  return result.data;
};

export const getSavedSearchGroupsRoot = (
  params: Record<string, unknown> = {}
) => {
  return apiClient.get(ecaEndpoint('/groups/root'), {params});
};

export const getSavedSearchChildren = (params: string, id: string) => {
  return apiClient.get(ecaEndpoint(`/groups/${id}` + params));
};

export const getSavedSearchFilter = (params: string) => {
  return apiClient.get(ecaEndpoint(`/groups/savedsearch` + params));
};

export const getSavedSearchDetails = (params: string, savedSerchId: string) => {
  return apiClient.get(ecaEndpoint(`/savedsearches/${savedSerchId}` + params));
};
export const updateSavedSearch = (
  params: string,
  groupid: string,
  reqBody: any
) => {
  return apiClient.put(ecaEndpoint(`/groups/${groupid}` + params), reqBody);
};

export const addNewTag = async (
  params: string,
  tagFieldId: string,
  reqBody: IAddNewTagBody[]
) => {
  const result = await apiClient.post(
    ecaEndpoint(`/${tagFieldId}/tag` + params),
    reqBody
  );
  return result;
};

export const addNewGroup = async (
  params: string,
  reqBody: IAddNewGroupReqBody
) => {
  const result = await apiClient.post(ecaEndpoint(`/groups` + params), reqBody);
  return result.data;
};

export const createSaveSearch = async (
  params: string,
  reqBody: ISavedSearchReqBody
) => {
  const result = await apiClient.post(
    ecaEndpoint(`/savedsearches` + params),
    reqBody
  );
  return result.data;
};

// API to get tag names based on tag field id
export const getTagNames = async (tagFieldId: string, params: string) => {
  return await apiClient.get(ecaEndpoint(`/field/${tagFieldId}` + params));
};

export const getSelectedTags = async (documentId: string, params: string) => {
  return await apiClient.get(
    ecaEndpoint(`/tagging/${documentId}/tag` + params)
  );
};

export const updateTagNamesForDocuments = (
  taggingType: string,
  params: string,
  requestBody: IUpdateTagNamesBody
) => {
  return apiClient.post(
    ecaEndpoint(`/tagging/documents/${taggingType}` + params),
    requestBody
  );
};

export const creatExports = async (
  type: string,
  params: string,
  reqBody: any
) => {
  const result = await apiClient.post(
    ecaEndpoint(`/export/${type}` + params),
    reqBody
  );
  return result.data;
};

export const getSearchTermReportDetails = (
  fieldTemplateId: string,
  reportId: string,
  params: string
) => {
  return apiClient.get(
    ecaEndpoint(
      `/indexes/${fieldTemplateId}/searchtermreports/${reportId}?` + params
    )
  );
};

export const getSearchTermRunResults = (
  fieldTemplateId: string,
  reportId: string,
  params: string
) => {
  return apiClient.get(
    ecaEndpoint(
      `/indexes/${fieldTemplateId}/searchtermreports/${reportId}/run/results?` +
        params
    )
  );
};

export const updateSaveSearchDetails = (
  params: string,
  savedSearchId: string,
  reqBody: any
) => {
  return apiClient.put(
    ecaEndpoint(`/savedsearches/${savedSearchId}` + params),
    reqBody
  );
};

export const savedSearchedRun = (
  params: string,
  indexName: string,
  savedSearchId: string,
  signal?: AbortSignal
) => {
  return apiClient.post(
    ecaEndpoint(
      `/indexes/${indexName}/savedSearches/${savedSearchId}/run` + params
    ),
    {},
    {signal: signal}
  );
};

export const getGroupDetails = (params: string, id: string) => {
  return apiClient.get(ecaEndpoint(`/groups/${id}/createdbydetails` + params));
};

export const deleteSavedSearch = (params: string, id: string) => {
  return apiClient.delete(ecaEndpoint(`/savedsearches/${id}` + params));
};

export const deleteSavedSearchGroup = (params: string, id: string) => {
  return apiClient.delete(ecaEndpoint(`/groups/${id}` + params));
};

export const updateSearchGroupDetails = (
  params: string,
  groupId: string,
  body: {name: string; parentGroupId: string}
) => {
  return apiClient.put(ecaEndpoint(`/groups/${groupId}` + params), body);
};

export const deleteSTRTerms = (
  fieldTemplateId: string,
  reportId: string,
  queryParam: string,
  body: {terms: string[]}
) => {
  return apiClient.post(
    ecaEndpoint(
      `/indexes/${fieldTemplateId}/searchtermreports/${reportId}/terms/delete?` +
        queryParam
    ),
    body
  );
};

export const runSTRTerms = (
  fieldTemplateId: string,
  reportId: string,
  queryParam: string,
  body: {runAllTermsFlag: boolean}
) => {
  return apiClient.post(
    ecaEndpoint(
      `/indexes/${fieldTemplateId}/searchtermreports/${reportId}/run?` +
        queryParam
    ),
    body
  );
};

export const stopSTRReport = (
  fieldTemplateId: string,
  reportId: string,
  queryParam: string
) => {
  return apiClient.post(
    ecaEndpoint(
      `/indexes/${fieldTemplateId}/searchtermreports/${reportId}/run/stop?` +
        queryParam
    )
  );
};

export const exportDocumentList = (
  params: string,
  searchId: string,
  reqBody: IDocListExportReqBody,
  signal?: AbortSignal
) => {
  return apiClient.post(
    ecaEndpoint(`/searches/results/export` + params),
    reqBody,
    {signal: signal}
  );
};

export const downloadExportDocument = (
  params: string,
  signal?: AbortSignal
) => {
  return apiClient.get(ecaEndpoint(`/document/download` + params), {
    signal: signal,
    responseType: 'blob',
  });
};

export const downloadSTR = (
  fieldTemplateId: string,
  reportId: string,
  queryParam: string,
  config: AxiosRequestConfig
) => {
  return apiClient.get(
    ecaEndpoint(
      `/indexes/${fieldTemplateId}/searchtermreports/${reportId}/run/exportresults?` +
        queryParam
    ),
    config
  );
};

export const getSTRDocList = (
  projectCode: string,
  reportId: string,
  body: any
) => {
  return apiClient.post(
    ecaEndpoint(`/searches/terms/${projectCode}/${reportId}`),
    body
  );
};

export const getQuerySearchValidate = (
  params: string,
  fieldId: number,
  body: any
) => {
  return apiClient.post(
    ecaEndpoint(`/indexes/${fieldId}/searches/validate?${params}`),
    body
  );
};

export const getUniqueValueForTransferList = (
  fieldId: number,
  queryParam: string
) => {
  return apiClient.get(
    ecaEndpoint(`/fields/${fieldId}/unique/values?` + queryParam)
  );
};

const normalizeDocumentIdFilter = (
  reqBody: IDocListReqBody
): IDocListReqBody => {
  const normalizeValue = (value: string | number | undefined) => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    const ids = trimmed
      .split(/\s+OR\s+/i)
      .flatMap((segment) => segment.trim().split(/\s+/))
      .map((id) => id.trim())
      .filter(Boolean);
    return ids.join(" OR ");
  };

  const processItems = (
    items: IFilterSet["items"]
  ): IFilterSet["items"] => {
    return items.map((item): IFilterSet["items"][number] => {
      
      if ("column" in item) {
        if (item.column.toLowerCase() === "documentid") {
          return {
            ...item,
            value: normalizeValue(item.value)
          };
        }
        return item;
      }

     
      return {
        ...item,
        items: item.items.map(
          (sub): { column: string; operator: string; value: string | number | undefined } => {
            if (sub.column.toLowerCase() === "documentid") {
              return {
                ...sub,
                value: normalizeValue(sub.value)
              };
            }
            return sub;
          }
        )
      };
    });
  };

  return {
    ...reqBody,
    filterSet: {
      ...reqBody.filterSet,
      items: processItems(reqBody.filterSet.items)
    }
  };
}

export const getMissingFamiliesDetails = (params: string) => {
  return apiClient.get(
    ecaEndpoint(`/validate/missingfamilies/`+ params)
  );
};

